import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Car, Users, Boxes } from "lucide-react";

export default function SearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setRes(null); return; }
      try {
        const { data } = await api.get("/search", { params: { q } });
        setRes(data); setOpen(true);
      } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };

  const total = res ? res.clients.length + res.vehicles.length + res.stock.length : 0;

  return (
    <div ref={boxRef} className="relative w-full">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Rechercher client, immatriculation, pièce…"
        className="pl-9 bg-[#0A0D14] border-[#2A364F] text-[#F8FAFC] w-full"
      />
      {open && res && (
        <div data-testid="search-results" className="absolute z-50 mt-2 w-full bg-[#121824] border border-[#2A364F] rounded-xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto">
          {total === 0 && <div className="px-4 py-6 text-sm text-[#64748B] text-center">Aucun résultat pour « {q} »</div>}

          {res.clients.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#64748B] bg-[#0A0D14] flex items-center gap-2"><Users className="w-3 h-3" />Clients</div>
              {res.clients.map((c) => (
                <button key={c.id} data-testid={`search-client-${c.id}`} onClick={() => go("/clients")} className="w-full text-left px-4 py-2.5 hover:bg-[#1E2A3E] flex items-center gap-3">
                  <span className="text-[#F8FAFC] font-medium">{c.nom}</span>
                  <span className="text-xs text-[#64748B]">{c.telephone}</span>
                </button>
              ))}
            </div>
          )}

          {res.vehicles.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#64748B] bg-[#0A0D14] flex items-center gap-2"><Car className="w-3 h-3" />Véhicules</div>
              {res.vehicles.map((v) => (
                <button key={v.id} data-testid={`search-vehicle-${v.id}`} onClick={() => go("/reparations")} className="w-full text-left px-4 py-2.5 hover:bg-[#1E2A3E] flex items-center gap-3">
                  <span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-2 py-0.5 rounded border border-yellow-500/30 uppercase text-xs">{v.immatriculation}</span>
                  <span className="text-[#F8FAFC]">{v.marque} {v.modele}</span>
                  {v.client_nom && <span className="text-xs text-[#64748B]">· {v.client_nom}</span>}
                </button>
              ))}
            </div>
          )}

          {res.stock.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#64748B] bg-[#0A0D14] flex items-center gap-2"><Boxes className="w-3 h-3" />Stock</div>
              {res.stock.map((s) => (
                <button key={s.id} data-testid={`search-stock-${s.id}`} onClick={() => go("/stock")} className="w-full text-left px-4 py-2.5 hover:bg-[#1E2A3E] flex items-center gap-3">
                  <span className="text-[#F8FAFC]">{s.designation}</span>
                  <span className="font-mono text-xs text-cyan-400">{s.reference}</span>
                  <span className="text-xs text-[#64748B] ml-auto">stock {s.quantite}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
